import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Stack,
  Paper,
  Divider,
  useTheme
} from '@mui/material';
import {
  FitnessCenter,
  ShowChart,
  Notifications,
  DeviceHub,
  Security,
  Speed
} from '@mui/icons-material';

const Home = () => {
  const theme = useTheme();

  const features = [
    {
      icon: <FitnessCenter fontSize="large" color="primary" />,
      title: 'Track Workouts',
      description: 'Log exercises, sets, reps, and weights with an intuitive interface.'
    },
    {
      icon: <ShowChart fontSize="large" color="primary" />,
      title: 'Monitor Progress',
      description: 'Visualize your fitness journey with detailed charts and analytics.'
    },
    {
      icon: <Notifications fontSize="large" color="primary" />,
      title: 'Set Reminders',
      description: 'Never miss a workout with customizable reminders and notifications.'
    },
    {
      icon: <DeviceHub fontSize="large" color="primary" />,
      title: 'Exercise Library',
      description: 'Access a comprehensive database of exercises with instructions.'
    },
    {
      icon: <Security fontSize="large" color="primary" />,
      title: 'Secure Data',
      description: 'Your fitness data is protected with enterprise-grade security.'
    },
    {
      icon: <Speed fontSize="large" color="primary" />,
      title: 'Performance Metrics',
      description: 'Track key performance indicators to optimize your training.'
    }
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Hero Section */}
      <Paper
        sx={{
          position: 'relative',
          backgroundColor: 'grey.800',
          color: '#fff',
          mb: 4,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundImage: 'url(https://source.unsplash.com/random?gym)',
          minHeight: '500px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {/* Increase the priority of the hero background image */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: 0,
            left: 0,
            backgroundColor: 'rgba(0,0,0,.5)',
          }}
        />
        <Container maxWidth="lg">
          <Grid container>
            <Grid item md={8} xs={12}>
              <Box
                sx={{
                  position: 'relative',
                  p: { xs: 3, md: 6 },
                  pr: { md: 0 },
                }}
              >
                <Typography component="h1" variant="h2" color="inherit" gutterBottom>
                  Track Your Fitness Journey
                </Typography>
                <Typography variant="h5" color="inherit" paragraph>
                  A comprehensive workout tracker to help you achieve your fitness goals.
                  Log workouts, track progress, and stay motivated with our powerful tools.
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                  <Button 
                    variant="contained" 
                    size="large" 
                    component={RouterLink} 
                    to="/register"
                    sx={{ px: 4, py: 1.5 }}
                  >
                    Get Started
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="large" 
                    component={RouterLink} 
                    to="/login"
                    sx={{ px: 4, py: 1.5, color: 'white', borderColor: 'white' }}
                  >
                    Sign In
                  </Button>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Paper>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ my: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom>
          Features
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" paragraph sx={{ mb: 6 }}>
          Everything you need to track and improve your fitness journey
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item key={index} xs={12} sm={6} md={4}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: theme.shadows[10],
                  }
                }}
              >
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                  {feature.icon}
                </Box>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography gutterBottom variant="h5" component="h3" align="center">
                    {feature.title}
                  </Typography>
                  <Typography align="center">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Call to Action */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 8,
          mt: 8
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h4" align="center" gutterBottom>
            Ready to start your fitness journey?
          </Typography>
          <Typography variant="h6" align="center" paragraph>
            Join thousands of users who have transformed their workouts with our tracking tools.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button 
              variant="contained" 
              color="secondary" 
              size="large" 
              component={RouterLink} 
              to="/register"
              sx={{ px: 4, py: 1.5 }}
            >
              Sign Up Now
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Container maxWidth="lg" sx={{ my: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom>
          What Our Users Say
        </Typography>
        <Divider sx={{ mb: 6 }} />
        <Grid container spacing={4}>
          {[1, 2, 3].map((item) => (
            <Grid item key={item} xs={12} md={4}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="body1" paragraph sx={{ fontStyle: 'italic' }}>
                    "This app has completely transformed how I track my workouts. The progress charts are incredibly motivating!"
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                    <CardMedia
                      component="img"
                      sx={{ width: 50, height: 50, borderRadius: '50%', mr: 2 }}
                      image={`https://randomuser.me/api/portraits/men/${item + 10}.jpg`}
                      alt="User avatar"
                    />
                    <Box>
                      <Typography variant="subtitle1" component="div">
                        John Doe {item}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Fitness Enthusiast
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Home;
